import { CSSResult, html, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './der-tailormade-roundtrip-offer.styles';

import DERModuleElement from '~/modules/utils/der-module-element';
import './calendar/default';
import './price-box';
import { RoundtripOfferData } from './roundtrip-interfaces';

enum View {
    PRICEBOX = 'pricebox',
    CALENDAR = 'calendar',
}

export class RoundtripOffer extends DERModuleElement {
    static override styles: CSSResult[] = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    @property({ type: String, attribute: 'analytics-name' }) analyticsName: string = '';
    @property({ type: Number, attribute: 'booking-day-offset' }) bookingDayOffset = 0;
    @property({ type: String, attribute: 'component-title' }) componentTitle: string = '';
    @property({ type: String, attribute: 'price-box-data-url' }) priceBoxDataUrl: string =
        'https://dtch-tailormade.azurewebsites.net/api/roundtrip-offer/best-offer';
    @property({ type: String, attribute: 'calendar-data-url' }) calendarDataUrl: string =
        'https://dtch-tailormade.azurewebsites.net/api/roundtrip-offer/offers';
    @property({ type: String, attribute: 'fallback-price' }) fallbackPrice: string = '';
    @property({ type: String, attribute: 'mode' }) mode: 'sticky' | 'default' = 'default';
    @property({ type: Object, attribute: 'offer-data' }) offerData: RoundtripOfferData = {};
    @property({ type: String, attribute: 'operator-name' }) operatorName: string = '';
    @property({ type: String, attribute: 'phone-number' }) phoneNumber: string = '';
    @property({ type: String, attribute: 'roundtrip-id' }) roundtripId: string = '';
    @property({ type: String, attribute: 'roundtrip-key' }) roundtripKey: string = '';
    @property({ type: String, attribute: 'roundtrip-types' }) roundtripTypes: string = '';
    @property({ type: String, attribute: 'view' }) view: View = View.PRICEBOX;
    @state() translation: object = {};

    protected override updated(_changedProperties: PropertyValues): void {
        if (_changedProperties.has('lang')) {
            this._loadTranslation();
        }
    }

    private async _loadTranslation(): Promise<void> {
        await this.loadTranslation(import.meta.url, `i18n/${this.lang}.json`);
    }

    private _handleCalendarSelection(event: CustomEvent): void {
        event.stopPropagation();
        this.emitEvent('der.roundtrip', event.detail);
    }

    private _handlePriceBoxClick(event: CustomEvent): void {
        event.stopPropagation();
        event.preventDefault();
        this.emitEvent('der.roundtrip-pricebox-click', event.detail);

        if (this.mode === 'sticky') {
            this.view = View.CALENDAR;
        }
    }

    override render(): TemplateResult | undefined {
        return html`
            <div>
                ${this.view === View.PRICEBOX
                    ? html`<der-tailormade-roundtrip-offer-price-box
                          lang=${this.lang}
                          currency=${this.currency}
                          brand=${this.brand}
                          tenant=${this.tenant}
                          data-url=${this.priceBoxDataUrl}
                          fallback-price=${this.fallbackPrice}
                          roundtrip-id=${this.roundtripId}
                          roundtrip-key=${this.roundtripKey}
                          roundtrip-types=${this.roundtripTypes}
                          component-title=${this.componentTitle}
                          analytics-name=${this.analyticsName}
                          operator-name=${this.operatorName}
                          ?previewMode=${this.mode === 'sticky'}
                          .offerData=${this.offerData}
                          @der.click=${this._handlePriceBoxClick}
                      ></der-tailormade-roundtrip-offer-price-box>`
                    : html``}
                ${this.view === View.CALENDAR
                    ? html`<der-tailormade-roundtrip-offer-calendar-default
                          lang=${this.lang}
                          analytics-name=${this.analyticsName}
                          currency=${this.currency}
                          data-url=${this.calendarDataUrl}
                          brand=${this.brand}
                          tenant=${this.tenant}
                          phone-number=${this.phoneNumber}
                          roundtrip-id=${this.roundtripId}
                          roundtrip-key=${this.roundtripKey}
                          booking-day-offset=${this.bookingDayOffset}
                          @der.selected=${this._handleCalendarSelection}
                      ></der-tailormade-roundtrip-offer-calendar-default>`
                    : html``}
            </div>
        `;
    }
}
